import { Component, OnInit, AfterViewInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginationInfo } from 'src/app/shared/models';
import { ExportCertificateService } from 'src/app/services/export-certificate/export-certificate.service';
import { ExportCertificateModalService } from 'src/app/services/export-certificate-modal/export-certificate-modal.service';
import Swal from 'sweetalert2';
import { GeneralService } from 'src/app/services/general/general.service';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-export-certificate-listing',
  templateUrl: './export-certificate-listing.component.html',
  styleUrls: ['./export-certificate-listing.component.scss']
})
export class ExportCertificateListingComponent extends DestructorComponent implements OnInit, AfterViewInit {

  @Output() certificateAdded = new EventEmitter<void>();
  @Output() certificateUpdated = new EventEmitter<void>();

  // Data properties
  certificates: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Make Math available in template
  Math = Math;

  // Filter properties
  filters = {
    search: '',
    date_from: '',
    date_to: ''
  };

  constructor(
    private _exportCertificateService: ExportCertificateService,
    private _exportCertificateModalService: ExportCertificateModalService,
    private cdr: ChangeDetectorRef,
    private _generalService: GeneralService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadCertificates();
  }

  ngAfterViewInit(): void {
    this.initializeMenus();
  }

  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  private loadCertificates(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    delete filters.search;
    Object.keys(filters).forEach(key => {
      if (filters[key] === null || filters[key] === undefined || filters[key] === '') {
        delete filters[key];
      }
    });

    this.subscriptions.push(
      this._exportCertificateService.GetData(page.toString(), search, filters).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.certificates = response.data.data || [];
            this.updatePaginationInfo(response.data);
          } else {
            this.certificates = [];
            this.resetPagination();
          }
          this.cdr.detectChanges();
          this.initializeMenus();
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading certificates:', error);
          this.certificates = [];
          this.resetPagination();
          this.cdr.detectChanges();
        }
      })
    );
  }

  private updatePaginationInfo(data: any): void {
    this.paginationInfo = {
      currentPage: data.current_page || 1,
      perPage: data.per_page || 20,
      total: data.total || 0,
      lastPage: data.last_page || 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    if (page !== this.currentPage && page >= 1 && page <= this.paginationInfo.lastPage) {
      this.loadCertificates(page, this.searchTerm, this.filters);
    }
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.filters.search = this.searchTerm;
    this.loadCertificates(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      search: '',
      date_from: '',
      date_to: ''
    };
    this.searchTerm = '';
    this.loadCertificates(1, '', {});
  }

  openAddCertificateModal(): void {
    this._exportCertificateModalService.clearSelectedCertificate();
    const modalElement = document.getElementById('kt_modal_vh_manager_export_certificate');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openViewCertificateModal(certificate: any): void {
    this._exportCertificateModalService.setSelectedCertificate(certificate);
    const modalElement = document.getElementById('kt_modal_view_export_certificate');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditCertificateModal(certificate: any): void {
    this._exportCertificateModalService.setSelectedCertificate(certificate);
    const modalElement = document.getElementById('kt_modal_vh_manager_export_certificate');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteCertificate(certificate: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete certificate "${certificate.certificate_no}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(certificate);
      }
    });
  }

  private performDelete(certificate: any): void {
    const certificateId = certificate._id || certificate.id;
    this.subscriptions.push(
      this._exportCertificateService.DeleteDataById(certificateId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Certificate has been deleted successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadCertificates(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete certificate',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error deleting certificate:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting certificate',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onCertificateAdded(): void {
    console.log('Certificate added, refreshing list...');
    this.loadCertificates(this.currentPage, this.searchTerm, this.filters);
    this.certificateAdded.emit();
  }

  onCertificateUpdated(): void {
    console.log('Certificate updated, refreshing list...');
    this.loadCertificates(this.currentPage, this.searchTerm, this.filters);
    this.certificateUpdated.emit();
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  getVehicleDisplayName(vehicle: any): string {
    if (!vehicle) return '-';
    const parts = [];
    if (vehicle.name) parts.push(vehicle.name);
    if (vehicle.company_name) parts.push(vehicle.company_name);
    if (vehicle.year) parts.push(`(${vehicle.year})`);
    return parts.join(' ') || 'Unknown Vehicle';
  }

  exportCertificate(certificate: any): void {

    let data = {
      certificate_id: certificate._id || certificate.id,
      type : 'export_certificate'
    }

    this._generalService.generatePDF(data).subscribe(res => {
      console.log('res-->', res)
    })

    // console.log('Exporting certificate:', certificate);
  }
}
